<script>
/**
 * KRX 원본 보관 (S3) — HQ 전용.
 *
 * 역할이 나뉘어 있다.
 *   · 올리기        — KRX 수신 서버. 파일이 거기 로컬에 있다.
 *   · 로컬 정리     — KRX 수신 서버. 한 달 지나고, 파싱·업로드가 끝났고,
 *                     그날 누락이 없는 파일만 자동으로 지운다.
 *   · 보기/내려받기/삭제 — 여기(관리서버).
 *
 * 로컬이 한 달이면 비므로 그 뒤로는 S3 사본이 마지막 한 부다. 끊긴 구간은
 * 코스콤이 재전송해 주지 않는다. 그래서 삭제는 파일명을 그대로 다시 입력해야
 * 실행된다 — 실수로 눌러서 지워지는 경로를 만들지 않는다.
 */
import {
  getKrxMeta,
  getArchiveObjects,
  getArchivePreview,
  downloadArchiveObject,
  deleteArchiveObject,
} from '@/api/krxMarket.js'

export default {
  name: 'KrxArchivePage',

  data() {
    return {
      serverIds: [],
      archiveConfigured: true,
      bucket: '',
      search: {
        serverId: '',
        bizDate: '',
      },
      objects: [],
      totalBytes: 0,
      loading: false,
      errorMessage: '',
      noticeMessage: '',

      /* 미리보기 */
      previewDialog: false,
      previewTarget: null,
      preview: null,
      previewLoading: false,

      /* 삭제 */
      deleteDialog: false,
      deleteTarget: null,
      deleteConfirm: '',
      deleting: false,
    }
  },

  computed: {
    deleteReady() {
      return this.deleteTarget && this.deleteConfirm === this.deleteTarget.fileName
    },
    previewTypes() {
      if (!this.preview?.countsByType) return []
      return Object.entries(this.preview.countsByType)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count)
    },
  },

  async mounted() {
    await this.loadMeta()
    if (this.search.serverId) {
      await this.fetchObjects()
    }
  },

  methods: {
    async loadMeta() {
      try {
        const res = await getKrxMeta()
        if (res.data?.code !== 0) {
          this.errorMessage = res.data?.message || '초기 정보를 가져오지 못했습니다.'
          return
        }
        const data = res.data.data || {}
        this.serverIds = data.serverIds || []
        this.search.serverId = this.serverIds[0] || ''
        this.search.bizDate = data.defaultBizDate || ''
        this.archiveConfigured = !!data.archiveConfigured
        this.bucket = data.archiveBucket || ''
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || '초기 정보를 가져오지 못했습니다.'
      }
    },

    async fetchObjects() {
      if (!this.search.serverId) return
      this.loading = true
      this.errorMessage = ''
      try {
        const res = await getArchiveObjects({
          serverId: this.search.serverId,
          bizDate: this.search.bizDate || undefined,
        })
        if (res.data?.code !== 0) {
          this.errorMessage = res.data?.message || '보관 목록 조회에 실패했습니다.'
          this.objects = []
          return
        }
        const data = res.data.data || {}
        this.objects = data.objects || []
        this.totalBytes = data.totalBytes || 0
        this.bucket = data.bucket || this.bucket
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || '보관 목록 조회 중 오류가 발생했습니다.'
      } finally {
        this.loading = false
      }
    },

    async openPreview(obj) {
      this.previewTarget = obj
      this.preview = null
      this.previewDialog = true
      this.previewLoading = true
      try {
        const res = await getArchivePreview({ key: obj.key, samples: 20 })
        if (res.data?.code === 0) {
          this.preview = res.data.data
        } else {
          this.errorMessage = res.data?.message || '미리보기에 실패했습니다.'
          this.previewDialog = false
        }
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || '미리보기 중 오류가 발생했습니다.'
        this.previewDialog = false
      } finally {
        this.previewLoading = false
      }
    },

    async download(obj) {
      try {
        const res = await downloadArchiveObject(obj.key)
        const blob = new Blob([res.data], { type: 'application/octet-stream' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        // 받아 놓고 나면 어느 날 몇 분인지 알 수 없어지므로 날짜를 붙인다.
        const parts = obj.key.split('/')
        a.download = parts.length >= 2 ? `${parts[parts.length - 2]}_${obj.fileName}` : obj.fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || '내려받기에 실패했습니다.'
      }
    },

    openDelete(obj) {
      this.deleteTarget = obj
      this.deleteConfirm = ''
      this.deleteDialog = true
    },

    async confirmDelete() {
      if (!this.deleteReady) return
      this.deleting = true
      try {
        const res = await deleteArchiveObject(this.deleteTarget.key, this.deleteConfirm)
        if (res.data?.code === 0) {
          this.noticeMessage = `${this.deleteTarget.fileName} 을(를) 삭제했습니다.`
          this.deleteDialog = false
          await this.fetchObjects()
        } else {
          this.errorMessage = res.data?.message || '삭제에 실패했습니다.'
        }
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || '삭제 중 오류가 발생했습니다.'
      } finally {
        this.deleting = false
      }
    },

    formatBytes(v) {
      if (!v) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      let n = Number(v)
      let i = 0
      while (n >= 1024 && i < units.length - 1) {
        n /= 1024
        i += 1
      }
      return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
    },

    formatNumber(v) {
      if (v === null || v === undefined) return '-'
      return Number(v).toLocaleString('ko-KR')
    },

    formatDateTime(iso) {
      if (!iso) return '-'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      return d.toLocaleString('ko-KR', { hour12: false })
    },
  },
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center mb-5">
      <div>
        <div class="text-h5 font-weight-bold">KRX 원본 보관</div>
        <div class="text-body-2 text-grey mt-1">
          S3 에 올라간 수신 전문 원본
          <span v-if="bucket" class="ml-1">· {{ bucket }}</span>
        </div>
      </div>
    </div>

    <v-alert v-if="!archiveConfigured" type="warning" variant="tonal" rounded="lg" class="mb-4">
      보관 버킷이 설정되지 않았습니다. 관리서버의 <code>krx.archive.bucket</code> 을 채우세요.
    </v-alert>

    <v-alert type="info" variant="tonal" rounded="lg" class="mb-4" density="compact">
      수신 서버의 로컬 파일은 <strong>한 달 뒤 자동으로 정리</strong>됩니다(파싱·업로드 완료,
      그날 누락 없음이 조건). 그 뒤로는 <strong>여기 S3 사본이 마지막 한 부</strong>입니다 —
      끊긴 구간은 코스콤이 재전송하지 않습니다.
    </v-alert>

    <!-- 검색 -->
    <v-card rounded="lg" elevation="0" border class="mb-5">
      <v-card-text class="pa-4">
        <v-row align="center" no-gutters>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-select
              v-model="search.serverId"
              :items="serverIds"
              label="수신 서버"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:160px;"
            />
          </v-col>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-text-field
              v-model="search.bizDate"
              type="date"
              label="영업일 (비우면 전체)"
              density="compact"
              variant="outlined"
              hide-details
              clearable
              style="min-width:200px;"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="primary"
              variant="flat"
              :loading="loading"
              :disabled="!archiveConfigured"
              @click="fetchObjects"
            >
              <v-icon start>mdi-magnify</v-icon>조회
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      rounded="lg"
      class="mb-4"
      closable
      @click:close="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>

    <v-alert
      v-if="noticeMessage"
      type="success"
      variant="tonal"
      rounded="lg"
      class="mb-4"
      closable
      @click:close="noticeMessage = ''"
    >
      {{ noticeMessage }}
    </v-alert>

    <!-- 목록 -->
    <v-card rounded="lg" elevation="0" border>
      <v-card-title class="pa-4 pb-2">
        <span class="text-subtitle-2 font-weight-bold">보관 객체</span>
        <span class="text-caption text-grey ml-2">
          {{ formatNumber(objects.length) }}건 · {{ formatBytes(totalBytes) }}
        </span>
      </v-card-title>
      <v-divider />
      <div v-if="loading" class="d-flex justify-center py-12">
        <v-progress-circular indeterminate color="amber-darken-2" size="40" />
      </div>
      <v-table v-else density="compact">
        <thead>
          <tr>
            <th style="width:110px;">파일</th>
            <th>키</th>
            <th class="text-right" style="width:110px;">크기</th>
            <th style="width:180px;">업로드 시각</th>
            <th style="width:130px;">보관 등급</th>
            <th style="width:200px;" class="text-right">작업</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in objects" :key="o.key">
            <td class="text-caption font-weight-medium">{{ o.fileName }}</td>
            <td class="text-caption text-grey">{{ o.key }}</td>
            <td class="text-right text-caption">{{ formatBytes(o.size) }}</td>
            <td class="text-caption">{{ formatDateTime(o.lastModified) }}</td>
            <td class="text-caption">{{ o.storageClass || 'STANDARD' }}</td>
            <td class="text-right">
              <v-btn size="x-small" variant="text" @click="openPreview(o)">
                <v-icon start size="small">mdi-eye-outline</v-icon>열람
              </v-btn>
              <v-btn size="x-small" variant="text" @click="download(o)">
                <v-icon start size="small">mdi-download</v-icon>받기
              </v-btn>
              <v-btn size="x-small" variant="text" color="error" @click="openDelete(o)">
                <v-icon start size="small">mdi-delete-outline</v-icon>삭제
              </v-btn>
            </td>
          </tr>
          <tr v-if="objects.length === 0">
            <td colspan="6" class="text-center text-grey py-8">보관된 원본이 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <!-- 미리보기 -->
    <v-dialog v-model="previewDialog" max-width="900">
      <v-card rounded="lg">
        <v-card-title class="pa-4">
          <span class="text-subtitle-1 font-weight-bold">
            {{ previewTarget?.fileName }} 전문 구성
          </span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <div v-if="previewLoading" class="d-flex justify-center py-10">
            <v-progress-circular indeterminate color="amber-darken-2" size="36" />
          </div>
          <template v-else-if="preview">
            <div class="mb-3">
              <v-chip size="small" class="mr-2" variant="tonal">
                전문 {{ formatNumber(preview.frameCount) }}건
              </v-chip>
              <v-chip size="small" class="mr-2" variant="tonal">
                {{ formatBytes(preview.byteCount) }}
              </v-chip>
              <v-chip v-if="preview.truncatedTail" size="small" color="warning" variant="tonal">
                끝에 구분자 없는 조각이 남아 있습니다
              </v-chip>
            </div>

            <div class="text-caption text-grey mb-1">전문 종류별 건수</div>
            <div class="d-flex flex-wrap ga-1 mb-4">
              <v-chip v-for="t in previewTypes" :key="t.type" size="small" variant="outlined">
                {{ t.type }} · {{ formatNumber(t.count) }}
              </v-chip>
              <span v-if="previewTypes.length === 0" class="text-caption text-grey">
                전문이 없습니다 (데이터가 없던 분의 빈 파일).
              </span>
            </div>

            <div v-if="preview.samples?.length" class="text-caption text-grey mb-1">
              앞부분 샘플
            </div>
            <v-sheet
              v-if="preview.samples?.length"
              rounded="lg"
              border
              class="pa-3 admin-code-block"
              style="max-height:320px;overflow:auto;"
            >
              <pre
                class="text-caption"
                style="white-space:pre-wrap;word-break:break-all;margin:0;"
              >{{ preview.samples.join('\n') }}</pre>
            </v-sheet>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn variant="text" @click="previewDialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 삭제 확인 -->
    <v-dialog v-model="deleteDialog" max-width="520">
      <v-card rounded="lg">
        <v-card-title class="pa-4 text-error">
          <v-icon start>mdi-alert-outline</v-icon>원본 삭제
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <p class="text-body-2 mb-3">
            <strong>{{ deleteTarget?.key }}</strong> 을(를) 영구히 지웁니다.
          </p>
          <p class="text-body-2 text-error mb-4">
            수신 서버의 로컬 사본은 한 달 뒤 자동으로 정리됩니다. 이 객체가 마지막
            한 부일 수 있고, 끊긴 구간은 코스콤이 재전송하지 않습니다.
          </p>
          <v-text-field
            v-model="deleteConfirm"
            :label="`확인을 위해 파일명(${deleteTarget?.fileName})을 입력하세요`"
            density="compact"
            variant="outlined"
            hide-details
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">취소</v-btn>
          <v-btn
            color="error"
            variant="flat"
            :disabled="!deleteReady"
            :loading="deleting"
            @click="confirmDelete"
          >
            삭제
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
